import {
  LayoutDashboard,
  Users,
  CalendarClock,
  PlaneTakeoff,
  MapPin,
  Building2,
  DoorOpen,
  BedDouble,
  Bookmark,
  GraduationCap,
  ClipboardList,
  ClipboardCheck,
  FileUp,
  BarChart3,
  FileEdit,
  History,
  ScanLine,
  Ticket,
  ArrowLeftRight,
  Table2,
} from "lucide-react";
import { RBAC } from "@/constants/rbac";
import { hasAnyPermission } from "@/lib/permissions";
import { isStudentSessionUser } from "@/lib/userRole";

/**
 * Central navigation registry:
 * - One RBAC key per UI component (constants/rbac.js)
 * - Each tab lists `childPermissions` (all component keys on that screen)
 * - Tab visible when user has ANY child key from login `rbac[]`
 * - Individual widgets gated with `<PermissionGate permission={RBAC.X}>`
 */

export const NAV_SECTIONS = {
  HR: "Human Resources",
  HOSTELS: "Hostels",
  STUDENT: "Leave",
};

/** OTP profile maps students to `role: "requester"`. Staff type always keeps staff UI. */
export function isStudentUser(user) {
  return isStudentSessionUser(user);
}

export const STUDENT_DEFAULT_HOME = "/hostels/apply-leave";

/** Student sidebar — only Apply Leave and My Leaves. */
export const STUDENT_NAV_ITEMS = [
  {
    section: NAV_SECTIONS.STUDENT,
    to: "/hostels/apply-leave",
    label: "Apply Leave",
    icon: FileEdit,
    key: "student-apply-leave",
    studentOnly: true,
    childPermissions: [],
  },
  {
    section: NAV_SECTIONS.STUDENT,
    to: "/hostels/my-leaves",
    label: "My Leaves",
    icon: History,
    key: "student-my-leaves",
    studentOnly: true,
    childPermissions: [],
  },
];

/** @typedef {import('lucide-react').LucideIcon} LucideIcon */

/**
 * @typedef {Object} NavItem
 * @property {string} to
 * @property {string} label
 * @property {LucideIcon} icon
 * @property {string} key
 * @property {string} [section]
 * @property {string} [navGroup] - sidebar dropdown id (sibling items merge)
 * @property {string} [navGroupLabel] - dropdown trigger label
 * @property {string} [navGroupIcon] - dropdown trigger icon
 * @property {readonly string[]} childPermissions - component keys inside this tab
 */

/** @type {NavItem[]} */
export const NAV_REGISTRY = [
  // ── HR ──────────────────────────────────────────────────────────
  {
    section: NAV_SECTIONS.HR,
    to: "/hr/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    key: "hr-dashboard",
    childPermissions: [
      RBAC.HR_DASHBOARD_STATS,
      RBAC.HR_DASHBOARD_ATTENDANCE_BREAKDOWN,
      RBAC.HR_DASHBOARD_ATTENDANCE_TABLE,
      RBAC.HR_DASHBOARD_ANOMALIES,
    ],
  },
  {
    section: NAV_SECTIONS.HR,
    to: "/hr/attendance",
    label: "Attendance",
    icon: CalendarClock,
    key: "hr-attendance",
    childPermissions: [
      RBAC.HR_ATTENDANCE_VIEW,
      RBAC.HR_ATTENDANCE_FILTERS,
      RBAC.HR_ATTENDANCE_STATS,
      RBAC.HR_ATTENDANCE_EDIT,
      RBAC.HR_ATTENDANCE_EXPORT,
    ],
  },
  {
    section: NAV_SECTIONS.HR,
    to: "/hr/leaves",
    label: "Leave Management",
    icon: PlaneTakeoff,
    key: "hr-leaves",
    childPermissions: [
      RBAC.HR_LEAVE_VIEW,
      RBAC.HR_LEAVE_APPROVE,
      RBAC.HR_LEAVE_REJECT,
    ],
  },
  {
    section: NAV_SECTIONS.HR,
    to: "/hr/geofences",
    label: "Geofences",
    icon: MapPin,
    key: "hr-geofences",
    childPermissions: [
      RBAC.HR_GEOFENCE_VIEW,
      RBAC.HR_GEOFENCE_CREATE,
      RBAC.HR_GEOFENCE_MANAGE,
    ],
  },
  {
    section: NAV_SECTIONS.HR,
    to: "/hr/employees",
    label: "Employees",
    icon: Users,
    key: "hr-employees",
    childPermissions: [RBAC.HR_EMPLOYEES_VIEW, RBAC.HR_EMPLOYEES_MANAGE],
  },

  // ── Hostels ─────────────────────────────────────────────────────
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    key: "hostels-dashboard",
    childPermissions: [RBAC.HOSTELS_DASHBOARD_VIEW],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/manage",
    label: "Hostels",
    icon: Building2,
    key: "hostels-manage",
    childPermissions: [RBAC.HOSTELS_MANAGE_VIEW, RBAC.HOSTELS_MANAGE_EDIT],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/rooms",
    label: "Rooms",
    icon: DoorOpen,
    key: "hostels-rooms",
    childPermissions: [RBAC.HOSTELS_ROOMS_VIEW, RBAC.HOSTELS_ROOMS_EDIT],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/layout",
    label: "Floor Plan",
    icon: BedDouble,
    key: "hostels-layout",
    childPermissions: [RBAC.HOSTELS_LAYOUT_VIEW, RBAC.HOSTELS_LAYOUT_EDIT],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/bed-sheet",
    label: "Bed Sheet",
    icon: Table2,
    key: "hostels-bed-sheet",
    childPermissions: [
      RBAC.HOSTELS_ALLOCATIONS_VIEW,
      RBAC.HOSTELS_ALLOCATIONS_EDIT,
    ],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/students",
    label: "Students",
    icon: GraduationCap,
    key: "hostels-students",
    childPermissions: [RBAC.HOSTELS_STUDENTS_VIEW, RBAC.HOSTELS_STUDENTS_EDIT],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/allocations",
    label: "Allocations",
    icon: ClipboardList,
    key: "hostels-allocations",
    childPermissions: [
      RBAC.HOSTELS_ALLOCATIONS_VIEW,
      RBAC.HOSTELS_ALLOCATIONS_EDIT,
    ],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/check-in",
    label: "Check-in Scanner",
    icon: ScanLine,
    key: "hostels-check-in",
    childPermissions: [RBAC.HOSTELS_CHECKIN_SCANNER_VIEW],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/gate-movements",
    label: "Gate movements",
    icon: ArrowLeftRight,
    key: "hostels-gate-movements",
    childPermissions: [RBAC.HOSTELS_GATE_MOVEMENTS_VIEW],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/token-desk",
    label: "Token Desk",
    icon: Ticket,
    key: "hostels-token-desk",
    childPermissions: [RBAC.HOSTELS_TOKEN_DESK_VIEW],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/reservations",
    label: "Reservations",
    icon: Bookmark,
    key: "hostels-reservations",
    childPermissions: [
      RBAC.HOSTELS_RESERVATIONS_VIEW,
      RBAC.HOSTELS_RESERVATIONS_EDIT,
    ],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/reports",
    label: "Reports & Analytics",
    icon: BarChart3,
    key: "hostels-reports",
    childPermissions: [RBAC.HOSTELS_REPORTS_VIEW],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/audit",
    label: "Audit Trail",
    icon: History,
    key: "hostels-audit",
    childPermissions: [RBAC.HOSTELS_AUDIT_VIEW],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/leaves",
    label: "Leave Approvals",
    icon: ClipboardCheck,
    key: "hostels-leaves",
    childPermissions: [RBAC.HOSTELS_LEAVES_VIEW, RBAC.HOSTELS_LEAVES_APPROVE],
  },
  {
    section: NAV_SECTIONS.HOSTELS,
    to: "/hostels/bulk-import",
    label: "Bulk Import",
    icon: FileUp,
    key: "hostels-bulk-import",
    childPermissions: [RBAC.HOSTELS_BULK_IMPORT_VIEW],
  },
];

/** Route path → component keys for that tab (used by PermissionRoute). */
export const ROUTE_PERMISSIONS = NAV_REGISTRY.reduce((acc, item) => {
  const path = item.to.split("#")[0].split("?")[0];
  const existing = acc[path] || [];
  acc[path] = [...new Set([...existing, ...item.childPermissions])];
  return acc;
}, {});

/** Sidebar label for a nav item. */
export function navItemLabel(item) {
  return item.label;
}

/** Dropdown child label — module name when tabs share a title across sections. */
export function navItemSublabel(entry, item) {
  if (entry.dropdownMode === "module" && item.section) return item.section;
  return item.label;
}

function sectionsDiffer(items) {
  const sections = new Set(items.map((i) => i.section).filter(Boolean));
  return sections.size > 1;
}

/** Route path → effective component keys. */
export function effectiveRoutePermissions(path) {
  const normalized = path.split("#")[0].split("?")[0];
  return ROUTE_PERMISSIONS[normalized] || null;
}

/** Tabs the user can access (has ≥1 component permission on that tab). */
export function navItemsForUser(user, permissions) {
  if (isStudentUser(user)) {
    return STUDENT_NAV_ITEMS;
  }
  return NAV_REGISTRY.filter((item) =>
    hasAnyPermission(permissions, item.childPermissions),
  );
}

/**
 * First route the user may access — used when redirecting from a denied route.
 * Returns null when nothing matches (caller must not treat this as "log out").
 */
export function firstAllowedPath(user, permissions) {
  if (isStudentUser(user)) {
    return STUDENT_DEFAULT_HOME;
  }
  return navItemsForUser(user, permissions)[0]?.to ?? null;
}

/** Build sidebar entries — nav groups, then merge duplicate labels (module sublabels). */
export function sidebarEntriesForGroup(items, user) {
  /** @type {Array<{ type: 'link', label: string, item: NavItem } | { type: 'dropdown', label: string, icon: LucideIcon, items: NavItem[], dropdownMode: 'label' | 'module' }>} */
  const entries = [];
  const groupIndex = new Map();
  const labelIndex = new Map();

  const pushLink = (label, item) => {
    labelIndex.set(label, entries.length);
    entries.push({ type: "link", label, item });
  };

  const pushDropdown = (label, icon, item, dropdownMode = "label") => {
    const idx = entries.length;
    if (item.navGroup) groupIndex.set(item.navGroup, idx);
    labelIndex.set(label, idx);
    entries.push({
      type: "dropdown",
      label,
      icon,
      items: [item],
      dropdownMode,
    });
  };

  const mergeIntoDropdown = (idx, item, dropdownMode) => {
    const entry = entries[idx];
    if (entry.type === "link") {
      const items = [entry.item, item];
      entries[idx] = {
        type: "dropdown",
        label: entry.label,
        icon: entry.item.icon,
        items,
        dropdownMode:
          dropdownMode === "module" || sectionsDiffer(items)
            ? "module"
            : "label",
      };
      return;
    }
    entry.items.push(item);
    if (dropdownMode === "module" || sectionsDiffer(entry.items)) {
      entry.dropdownMode = "module";
    }
  };

  for (const item of items) {
    if (item.navGroup) {
      const groupLabel = item.navGroupLabel || item.label;
      const groupIcon = item.navGroupIcon || item.icon;
      if (groupIndex.has(item.navGroup)) {
        entries[groupIndex.get(item.navGroup)].items.push(item);
        continue;
      }
      pushDropdown(groupLabel, groupIcon, item, "label");
      continue;
    }

    const label = navItemLabel(item);
    if (!labelIndex.has(label)) {
      pushLink(label, item);
      continue;
    }

    const idx = labelIndex.get(label);
    const existing = entries[idx];
    const existingItem =
      existing.type === "link" ? existing.item : existing.items[0];
    const crossModule = existingItem.section !== item.section;
    mergeIntoDropdown(idx, item, crossModule ? "module" : "label");
  }

  return entries.map((entry) => {
    if (entry.type === "dropdown" && entry.items.length === 1) {
      const only = entry.items[0];
      return {
        type: "link",
        label: only.navGroup ? entry.label : navItemLabel(only),
        item: only.navGroup ? { ...only, label: entry.label } : only,
      };
    }
    return entry;
  });
}

/** Group visible nav items by section for sidebar rendering. */
export function groupedNavForUser(user, permissions) {
  const items = navItemsForUser(user, permissions);
  const seen = new Set();
  const deduped = items.filter((item) => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });

  const globalEntries = sidebarEntriesForGroup(deduped, user);

  const groups = [];
  let currentSection = null;
  for (const entry of globalEntries) {
    const section =
      entry.type === "link" ? entry.item.section : entry.items[0].section;
    if (section !== currentSection) {
      currentSection = section;
      groups.push({ section: currentSection, entries: [] });
    }
    groups[groups.length - 1].entries.push(entry);
  }

  return groups;
}
